import { Alert, Button } from "@mui/material"
import { useEffect, useState } from "react"
import { useHistory } from "react-router-dom"
import { toast } from "react-toastify"
import { useRecoilValue, useResetRecoilState, useRecoilState, useRecoilCallback } from "recoil"
import { MerSETASDEService } from "../../../../../Component/Api/merSETA/External/MerSETASDEService"
import { MerSETALAIService } from "../../../../../Component/Api/merSETA/LAI/MerSETALAIService"
import { ConfirmationDialog } from "../../../../../Component/Common/Components/ConfirmationDialog"
import { DataTable } from "../../../../../Component/Common/Components/DataTable/DataTable"
import { FilterType, IFilter } from "../../../../../Component/Common/Components/DataTable/Filters"
import { ColumnSortOption } from "../../../../../Component/Model/Dto/ColumnSortOption"
import { filterColumnValues } from "../../../../../Component/Model/Dto/ScorecardLicense"
import { MerSETASdeDto, MerSetaSDESearchRequest } from "../../../../../Component/Model/Dto/WSP/MerSETASDE"
import { EntriesPerPage } from "../../../../../Component/Model/Table/Table"
import { defaultObject, isEmptyString } from "../../../../../Helpers/typescriptHelper"
import { OrganisationAtom } from "../../../../../State/Organisation.Atom"
import { OrganisationExternalTrainingDataTable } from "./OrganisationExternalTrainingDataTable"
import { InterventionAtom } from "../Intervention.Atom"
import { TrainingImportButtons } from "../Import/TrainingImportButtons"
import { DataImportType } from "../../../../../Component/Model/Enum/BEE123Enums"

export const OrganisationExternalTraining = () => {
  const history = useHistory();
  const peopleAndTrainingOrganisation = useRecoilValue(OrganisationAtom);
  const [platformTrainingData, setPlatformTrainingData] = useState<MerSETASdeDto[]>([]);
  const [entriesPerPage, setEntriesPerPage] = useState<EntriesPerPage>({ defaultValue: 10 });
  const [totalItemsCount, setTotalItemsCount] = useState<number>(0);
  const [filteredItemCount, setFilteredItemCount] = useState<number>(0);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [columnSortOptions, setColumnSortOptions] = useState<ColumnSortOption>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [interventionTypeId, setInterventionTypeId] = useState<number[]>();
  const [interventionTitleId, setInterventionTitleId] = useState<number[]>();
  const [startDate, setStartDate] = useState<Date>();
  const [endDate, setEndDate] = useState<Date>();
  const [searchValue, setSearchValue] = useState<string>("");
  const [hasNoSeta, setHasNoSeta] = useState<boolean>(false);
  const [interventionTitle, setInterventionTitle] = useState<filterColumnValues[]>(defaultObject);
  const [interventionType, setInterventionType] = useState<filterColumnValues[]>(defaultObject);
  const [beeCategoryId, setBeeCategoryId] = useState<number[]>();
  const [beeCategory, setBeeCategory] = useState<filterColumnValues[]>(defaultObject);
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
  const [selectedInterventionId, setSelectedInterventionId] = useState(-1);
  const [selectedCourseName, setSelectedCourseName] = useState<string>();
  const confirmDeleteMessage = `Are you sure you want to delete the course ${selectedCourseName}?`;

  const resetSDEAtom = useResetRecoilState(InterventionAtom);
  const setSelectedSDE = useRecoilCallback(({ set }) => (id: number) => {
    set(InterventionAtom, { id });
  });


  const externalTrainingFilters: Array<IFilter> = [
    {
      label: 'Date',
      values: [],
      type: FilterType.dateRange, propname: 'startEndDate',
    },
    {
      values: beeCategory,
      label: 'BEE Categories:',
      type: FilterType.select, propname: 'beeCategory'
    },
    {
      label: 'Intervention Type:',
      values: interventionType,
      type: FilterType.select, propname: 'interventionTypeId'
    },
    {
      label: 'Intervention Title:',
      values: interventionTitle,
      type: FilterType.select, propname: 'interventionTitleId'
    }
  ]

  const externalTrainingSearchRequest: MerSetaSDESearchRequest = {
    organisationId: peopleAndTrainingOrganisation.OrganisationId,
    searchValue: searchValue,
    interventionTitleId: interventionTitleId,
    interventionTypeId: interventionTypeId,
    beeCategoryId: beeCategoryId,
    startDate: startDate,
    endDate: endDate,
    columnSortOption: columnSortOptions,
    pageNumber: pageNumber,
    pageSize: entriesPerPage.defaultValue!
  }

  useEffect(() => {
    if (peopleAndTrainingOrganisation.OrganisationId > 0) {
      fetchTraining();

      if ((peopleAndTrainingOrganisation.SetaTypeId !== "0" || isEmptyString(peopleAndTrainingOrganisation.SetaTypeId))) {
        setHasNoSeta(true);
      }
    }

  }, [peopleAndTrainingOrganisation,
    entriesPerPage,
    pageNumber,
    columnSortOptions,
    searchValue,
    startDate,
    endDate,
    beeCategoryId,
    interventionTitleId,
    interventionTypeId]);

  useEffect(() => {
    fetchStaticData();
  }, [])

  const fetchStaticData = async () => {
    try {
      const resultTitlesPromise = await MerSETALAIService.GetMerSETAInterventionTitles();
      const resultCatgoriesPromise = await MerSETASDEService.GetMerSETACategoryInterventions();


      const [interventionTitle, beeCategories] = await Promise.all([
        resultTitlesPromise,
        resultCatgoriesPromise
      ])

      const interventionTitleData: filterColumnValues[] = interventionTitle.data.map((data) => {
        return { key: data.description, val: data.code };
      })

      const beeCategoriesData: filterColumnValues[] = beeCategories.data.map((data) => {
        return { key: data.category, val: data.id };
      })

      const interventionTypeData: filterColumnValues[] = beeCategories.data.map((data) => {
        return data.interventionTypes.map(intervention => {
          return { key: intervention.description, val: intervention.code }
        });
      }).flat();


      setInterventionTitle(interventionTitleData);
      setInterventionType(interventionTypeData);
      setBeeCategory(beeCategoriesData);

    } catch (error) {
      console.error(error);
    }
  }

  const showDeleteConfirmationModal = (id: number, courseName: string) => {
    setSelectedInterventionId(id);
    setSelectedCourseName(courseName);
    setShowConfirmationDialog(true);
  }

  const handleCloseConfirmationModal = () => {
    setShowConfirmationDialog(false);
    setSelectedInterventionId(-1);
    fetchTraining();
  }

  const handleDelete = async () => {
    try {
      await MerSETASDEService.Delete(selectedInterventionId);
      toast.success("Successfully deleted intervention");
    } catch (error) {
      console.log(error)
    } finally {
      handleCloseConfirmationModal();
    }
  }

  const entriesPerPageChanged = (entriesPerPage: number) => {
    setEntriesPerPage({ defaultValue: entriesPerPage });
    setPageNumber(1);
  };

  const pageNumberChanged = (pageNumber: number) => {
    setPageNumber(pageNumber);
  };

  const columnSortingChanged = (sortOption: ColumnSortOption) => {
    setColumnSortOptions(sortOption)
    setPageNumber(1);
  };

  const searchValueChanged = (value: string) => {
    setSearchValue(value);
    setPageNumber(1);
  }

  useEffect(() => {
    resetSDEAtom();
  }, [])

  const fetchTraining = async () => {
    setIsLoading(true);
    try {
      var results = await MerSETASDEService.Search(externalTrainingSearchRequest);
      if (results.data != null) {
        setPlatformTrainingData(results.data.results);
        setFilteredItemCount(results.data.filteredItemsCount);
        setTotalItemsCount(results.data.totalItemsCount);
      }
    } catch (error) {
      console.error(error);
      setTotalItemsCount(1);
    } finally {
      setIsLoading(false);
    }
  }

  const filtersChanged = async (filterProps: any) => {
    const { start, end } = filterProps['startEndDate'] || { start: null, end: null };
    const interventionTypeId = filterProps['interventionTypeId'] || null;
    const interventionTitleId = filterProps['interventionTitleId'] || null;
    const beeCategory = filterProps['beeCategory'] || null;
    const startDate = start ? new Date(start) : undefined;
    const endDate = end ? new Date(end) : undefined;

    setInterventionTypeId(interventionTypeId);
    setInterventionTitleId(interventionTitleId);
    setBeeCategoryId(beeCategory);
    setStartDate(startDate);
    setEndDate(endDate)
    setPageNumber(1);
  };


  const navigateToMerSetaSdePage = (merSetaSdeId: number) => {
    setSelectedSDE(merSetaSdeId);
    history.push(`/organisation/training/External/form/Details`);
  }

  const handleRouteToAttendeeClick = (merSetaSdeId: number) => {
    setSelectedSDE(merSetaSdeId);
    history.push(`/organisation/training/External/form/Attendees`);
  }

  const organisationAndTrainingTableData = OrganisationExternalTrainingDataTable(platformTrainingData, showDeleteConfirmationModal, navigateToMerSetaSdePage, handleRouteToAttendeeClick);

  return (
    <>
      {hasNoSeta ?
        <>

          <Button color="primary" variant="contained" onClick={() => navigateToMerSetaSdePage(0)}>CAPTURE EXTERNAL TRAINING</Button>
          <DataTable
            key={totalItemsCount}
            table={organisationAndTrainingTableData}
            entriesPerPage={entriesPerPage}
            pageNumber={pageNumber}
            filteredItemsCount={filteredItemCount}
            totalItemsCount={totalItemsCount}
            filters={externalTrainingFilters}
            entriesPerPageChanged={entriesPerPageChanged}
            pageNumberChanged={pageNumberChanged}
            searchValueChanged={searchValueChanged}
            isLoading={isLoading}
            canSearch
            filtersChanged={filtersChanged}
            manualSorting={true}
            columnSortingChanged={columnSortingChanged}
            searchBoxText='Search course name...'
          />
          <TrainingImportButtons dataImportType={DataImportType.MerSetaTraining} organisationId={peopleAndTrainingOrganisation.OrganisationId} fetchData={fetchTraining}/>

        </>
        :
        <>
          <Alert severity="warning" style={{ paddingTop: 5 }}>
            In order to capture accurate training data in alignment with your SETA, please request your administrator to capture a SETA on an organisation.
          </Alert>
        </>
      }
      <ConfirmationDialog
        showDialog={showConfirmationDialog}
        handleAccept={handleDelete}
        handleCancel={handleCloseConfirmationModal}
        heading={"Delete External Training"}
        message={confirmDeleteMessage}
      />

    </>
  )
}
